import { useWordPressAuth } from "@/components/wordpress-auth-provider"
import { UserAvatar } from "@/components/user-avatar"
import { getUserProfile, updateUserProfile, uploadProfilePicture, UserProfileData } from "@/lib/user-profile"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Settings, User, Camera, Save, Loader2 } from "lucide-react"
import { useState, useEffect, useRef } from "react"
import { useToast } from "@/hooks/use-toast"

export default function SettingsPage() {
  const { user, refreshUser } = useWordPressAuth()
  const { toast } = useToast()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [profileData, setProfileData] = useState<UserProfileData>({
    firstName: '',
    lastName: '',
    profilePicture: ''
  })
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setSaving] = useState(false)
  const [isUploading, setUploading] = useState(false)

  // Load profile data on mount
  useEffect(() => {
    loadProfileData()
  }, [])

  const loadProfileData = async () => {
    try {
      setIsLoading(true)
      const data = await getUserProfile()
      const isDev = (window as any).WP_COCKPIT?.devMode || false
      if (isDev) console.log('[SettingsPage] Profile data loaded:', data)
      setProfileData(data)
    } catch (error) {
      console.error('Failed to load profile data:', error)
      toast({
        title: "Error",
        description: "Failed to load profile data"
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleSaveProfile = async () => {
    try {
      setSaving(true)
      await updateUserProfile({
        firstName: profileData.firstName,
        lastName: profileData.lastName
      })
      
      // Refresh user data in context
      await refreshUser()
      
      toast({
        title: "Success",
        description: "Profile updated successfully"
      })
    } catch (error) {
      console.error('Failed to update profile:', error)
      toast({
        title: "Error", 
        description: "Failed to update profile"
      })
    } finally {
      setSaving(false)
    }
  }

  const handleFileSelect = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    try {
      setUploading(true)
      const response = await uploadProfilePicture(file)
      
      if (response.success && response.data?.profilePicture) {
        setProfileData(prev => ({
          ...prev,
          profilePicture: response.data!.profilePicture
        }))
        
        // Refresh user data in context
        await refreshUser()
        
        toast({
          title: "Success",
          description: "Profile picture uploaded successfully"
        })
      }
    } catch (error) {
      console.error('Failed to upload profile picture:', error)
      toast({
        title: "Error",
        description: "Failed to upload profile picture"
      })
    } finally {
      setUploading(false)
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Settings</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Manage your cockpit preferences and profile
          </p>
        </div>

        {/* Profile Settings */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-10 w-10 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
              <User className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Profile Settings</h2>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-gray-600 dark:text-gray-400" />
              <span className="ml-2 text-gray-600 dark:text-gray-400">Loading profile...</span>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Profile Picture Section */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="flex items-center gap-4">
                  <UserAvatar 
                    displayName={user?.displayName}
                    avatarUrl={user?.avatarUrl}
                    profilePicture={profileData.profilePicture || user?.profilePicture}
                    className="h-20 w-20"
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Profile Picture</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">JPG, PNG or GIF (max 2MB)</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleFileSelect}
                    disabled={isUploading}
                    className="flex items-center gap-2"
                  >
                    {isUploading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Camera className="h-4 w-4" />
                    )}
                    {isUploading ? 'Uploading...' : 'Change Picture'}
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </div>
              </div>

              {/* Name Fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    First Name
                  </label>
                  <Input
                    type="text"
                    value={profileData.firstName}
                    onChange={(e) => setProfileData(prev => ({ ...prev, firstName: e.target.value }))}
                    placeholder="Enter your first name"
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Last Name
                  </label>
                  <Input
                    type="text"
                    value={profileData.lastName}
                    onChange={(e) => setProfileData(prev => ({ ...prev, lastName: e.target.value }))}
                    placeholder="Enter your last name"
                    className="w-full"
                  />
                </div>
              </div>

              {/* Read-only fields */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Display Name
                  </label>
                  <Input
                    type="text"
                    value={user?.displayName || ''}
                    disabled
                    className="bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Auto-updated from first and last name
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email
                  </label>
                  <Input
                    type="email"
                    value={user?.email || ''}
                    disabled
                    className="bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Managed by admin
                  </p>
                </div>
              </div>

              {/* Save Button */}
              <div className="flex justify-end pt-4 border-t dark:border-gray-700">
                <Button
                  onClick={handleSaveProfile}
                  disabled={isSaving}
                  className="flex items-center gap-2"
                >
                  {isSaving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Info Box */}
        <div className="bg-gray-100 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-start gap-3">
            <Settings className="h-5 w-5 text-gray-600 dark:text-gray-400 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Settings Coming Soon</p>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                More customization options will be added in future updates. 
                Stay tuned for enhanced cockpit features!
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
